import { createSlice } from '@reduxjs/toolkit';

const toYmd = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const past30DayRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return { date_from: toYmd(from), date_to: toYmd(to) };
};

const defaultRange = past30DayRange();

const emptyListFilters = {
    customer_type: '',
    branch_id: '',
    date_from: defaultRange.date_from,
    date_to: defaultRange.date_to,
    sort_field: 'last_order_date',
    sort_direction: 'desc',
};

const emptyPaymentFilters = {
    branch_id: '',
    date_from: defaultRange.date_from,
    date_to: defaultRange.date_to,
    sort_field: 'payment_date',
    sort_direction: 'desc',
};

const initialState = {
    stats: [],
    legacyStats: [],
    yearlySales: null,
    salesByLocation: null,
    branchComparison: [],
    branchOptions: [],
    topEmployees: [],
    recentCustomers: [],
    recentPayments: [],
    recentStockUsage: [],
    revenueTrend: [],
    paymentMethodsSummary: [],
    stockUsageTrend: [],
    lowStockItems: [],
    dateRange: {
        date_from: past30DayRange().date_from,
        date_to: past30DayRange().date_to,
    },
    customerFilters: { ...emptyListFilters },
    paymentFilters: { ...emptyPaymentFilters },
    loading: {
        stats: false,
        legacyStats: false,
        yearlySales: false,
        salesByLocation: false,
        branchComparison: false,
        topEmployees: false,
        recentCustomers: false,
        recentPayments: false,
        recentStockUsage: false,
        revenueTrend: false,
        paymentMethodsSummary: false,
        stockUsageTrend: false,
    },
    errors: {
        stats: null,
        legacyStats: null,
        yearlySales: null,
        salesByLocation: null,
        branchComparison: null,
        topEmployees: null,
        recentCustomers: null,
        recentPayments: null,
        recentStockUsage: null,
        revenueTrend: null,
        paymentMethodsSummary: null,
        stockUsageTrend: null,
    },
    selectedYear: new Date().getFullYear(),
};

const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setWidgetLoading: (state, action) => {
            const { key, value } = action.payload;
            state.loading[key] = value;
        },
        setWidgetError: (state, action) => {
            const { key, error } = action.payload;
            state.errors[key] = error;
            state.loading[key] = false;
        },
        setStats: (state, action) => {
            state.stats = action.payload;
            state.loading.stats = false;
            state.errors.stats = null;
        },
        setLegacyStats: (state, action) => {
            state.legacyStats = action.payload;
            state.loading.legacyStats = false;
            state.errors.legacyStats = null;
        },
        setYearlySales: (state, action) => {
            state.yearlySales = action.payload;
            state.loading.yearlySales = false;
            state.errors.yearlySales = null;
        },
        setSalesByLocation: (state, action) => {
            state.salesByLocation = action.payload;
            state.loading.salesByLocation = false;
            state.errors.salesByLocation = null;
        },
        setBranchComparison: (state, action) => {
            state.branchComparison = action.payload.data || [];
            state.branchOptions = action.payload.branches || [];
            state.loading.branchComparison = false;
            state.errors.branchComparison = null;
        },
        setTopEmployees: (state, action) => {
            state.topEmployees = action.payload;
            state.loading.topEmployees = false;
            state.errors.topEmployees = null;
        },
        setRecentCustomers: (state, action) => {
            state.recentCustomers = action.payload;
            state.loading.recentCustomers = false;
            state.errors.recentCustomers = null;
        },
        setRecentPayments: (state, action) => {
            state.recentPayments = action.payload;
            state.loading.recentPayments = false;
            state.errors.recentPayments = null;
        },
        setRecentStockUsage: (state, action) => {
            state.recentStockUsage = action.payload;
            state.loading.recentStockUsage = false;
            state.errors.recentStockUsage = null;
        },
        setRevenueTrend: (state, action) => {
            state.revenueTrend = action.payload;
            state.loading.revenueTrend = false;
            state.errors.revenueTrend = null;
        },
        setPaymentMethodsSummary: (state, action) => {
            state.paymentMethodsSummary = action.payload.data || [];
            state.loading.paymentMethodsSummary = false;
            state.errors.paymentMethodsSummary = null;
        },
        setStockUsageTrend: (state, action) => {
            state.stockUsageTrend = action.payload.data || [];
            state.lowStockItems = action.payload.low_stock_items || [];
            state.loading.stockUsageTrend = false;
            state.errors.stockUsageTrend = null;
        },
        setDateRange: (state, action) => {
            state.dateRange = { ...state.dateRange, ...action.payload };
        },
        setCustomerFilters: (state, action) => {
            state.customerFilters = { ...state.customerFilters, ...action.payload };
        },
        setPaymentFilters: (state, action) => {
            state.paymentFilters = { ...state.paymentFilters, ...action.payload };
        },
        setSelectedYear: (state, action) => {
            state.selectedYear = action.payload;
        },
        resetDashboard: () => initialState,
    },
});

export const {
    setWidgetLoading,
    setWidgetError,
    setStats,
    setLegacyStats,
    setYearlySales,
    setSalesByLocation,
    setBranchComparison,
    setTopEmployees,
    setRecentCustomers,
    setRecentPayments,
    setRecentStockUsage,
    setRevenueTrend,
    setPaymentMethodsSummary,
    setStockUsageTrend,
    setDateRange,
    setCustomerFilters,
    setPaymentFilters,
    setSelectedYear,
    resetDashboard,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;
